// Generated by GitHub Copilot
import { SidebarState, useSidebarCollapse } from '#core/state/ui/SidebarState.ts'
import { useSessionState } from '#core/state/State.ts'
import { cn } from '#core/utils/cn.ts'
import { createFileRoute, Link, Outlet, useLocation, useRouter } from '@tanstack/react-router'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, Download } from 'lucide-react'
import { YnteqMark, YnteqWordmark } from '#components/primitives/icons/ynteq-logo.tsx'
import { PermissionGuard } from '#components/tools/permission-guard.tsx'
import { PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { readGatePairs } from '#core/entities/auth/roles/permission-gate-helpers.ts'
import { useAppMode } from '#hooks/use-app-mode.ts'
import { usePwaInstall } from '#hooks/use-pwa-install.ts'
import {
  STATIC_PANEL_ROUTES,
  useSettingsNavSections,
} from '#components/domain/operations/settings/settings-nav-sections.ts'
import { SettingsMobileList } from '#components/domain/operations/settings/settings-mobile-list.tsx'
import { useCanonicalCompanyUrl, useUserState } from '#core/state/UserState.ts'
import {
  CollapsibleSidebar,
  SidebarGroup,
  SidebarRow,
  SidebarRowContent,
  SidebarSection,
  sidebarRowClass,
  useSidebarContext,
} from '#components/composites/collapsible-sidebar/index.ts'

export const Route = createFileRoute('/_appSettings/$companyId/settings')({
  component: RouteComponent,
})

/** A single settings-rail link, routed via the static table or the `$panel` fallback. */
function SettingsNavLink({
  icon,
  label,
  companyId,
  panel,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  companyId: string
  panel: string
}) {
  const { collapsed } = useSidebarContext()
  const linkClass = sidebarRowClass(collapsed)
  const activeProps = { className: cn(linkClass, 'active') }
  const content = <SidebarRowContent icon={icon} label={label} />
  const staticTo = STATIC_PANEL_ROUTES[panel as keyof typeof STATIC_PANEL_ROUTES]

  return (
    <SidebarRow>
      {staticTo ? (
        <Link to={staticTo} params={{ companyId }} activeOptions={{ exact: true }} className={linkClass} activeProps={activeProps}>
          {content}
        </Link>
      ) : (
        <Link to="/$companyId/settings/$panel" params={{ companyId, panel }} activeOptions={{ exact: true }} className={linkClass} activeProps={activeProps}>
          {content}
        </Link>
      )}
    </SidebarRow>
  )
}

function RouteComponent() {
  const { companyId } = Route.useParams()
  // Upgrade a GUID company segment in the URL to the readable company code once it's known.
  useCanonicalCompanyUrl()
  const { t } = useTranslation('settings')
  const { t: tGeneral } = useTranslation('general')
  const { t: tNav } = useTranslation('navigation')
  const sidebarState = useSessionState(SidebarState, 'sbs')
  const sidebarCollapse = useSidebarCollapse()
  const collapsed = sidebarCollapse.collapsed
  const router = useRouter()
  const appMode = useAppMode()
  const userState = useUserState()
  const location = useLocation()
  const pwa = usePwaInstall()
  const navSections = useSettingsNavSections()

  const canReadWorkOrders = userState.hasAnyPermission(readGatePairs(PermissionService.WORK_ORDERS))
  // Same fallback rule the landing redirect uses: app mode → Work Requests;
  // desktop → Work Orders if readable, else Work Requests. `lastAppPath` still
  // wins whenever it was recorded, so this only affects the deep-linked-straight-
  // into-settings case.
  const backToAppFallback = appMode
    ? `/${companyId}/work-requests`
    : canReadWorkOrders ? `/${companyId}/work-orders` : `/${companyId}/work-requests`
  const backToAppPath = sidebarState.lastAppPath ?? backToAppFallback

  // The mobile list is the settings "home" on a phone; a panel path is anything
  // deeper than `/<companyId>/settings`.
  const settingsRoot = `/${companyId}/settings`
  const onPanel = location.pathname.replace(/\/$/, '') !== settingsRoot

  if (appMode) {
    // Panel open → full-screen panel with a Back-to-list bar (mirrors the
    // split-detail full-screen pattern). Root → the mobile section list.
    if (onPanel) {
      return (
        <div className="d-flex flex-column vh-100">
          <header className="ynt-app-topbar ynt-app-topbar-row d-flex align-items-center gap-2 px-2 border-bottom flex-shrink-0">
            <Link
              to="/$companyId/settings"
              params={{ companyId }}
              className="btn btn-sm btn-icon p-1 border-0 bg-transparent text-decoration-none flex-shrink-0"
              aria-label={t('mobile.backToList')}
            >
              <ChevronLeft size={22} />
            </Link>
            <span className="fw-semibold flex-grow-1 text-truncate">{t('mobile.title')}</span>
          </header>
          <div className="flex-grow-1 overflow-y-auto mw-100">
            <Outlet />
          </div>
        </div>
      )
    }
    return (
      <SettingsMobileList
        companyId={companyId}
        backToAppPath={backToAppPath}
        onBackToApp={() => router.history.push(backToAppPath)}
      />
    )
  }

  return (
    <div className="d-flex vh-100">
      <CollapsibleSidebar
        collapsed={collapsed}
        onToggleCollapse={() => sidebarCollapse.toggle()}
        ariaLabel={t('mobile.title')}
        expandLabel={tNav('side_menu.expandSidebar')}
        collapseLabel={tNav('side_menu.collapseSidebar')}
        brand={<YnteqWordmark height={26} className="flex-shrink-0" style={{ color: 'var(--ynt-brand-ink)' }} />}
        brandCollapsed={<YnteqMark height={26} className="flex-shrink-0" style={{ color: 'var(--ynt-brand-ink)' }} />}
      >
        <SidebarGroup>
          {/* Back to app — returns to the last visited in-app page. */}
          <SidebarRow>
            <button
              type="button"
              onClick={() => router.history.push(backToAppPath)}
              className={cn(sidebarRowClass(collapsed), 'border-0 w-100')}
              aria-label={t('shared.actions.backToApp')}
            >
              <SidebarRowContent icon={ChevronLeft} label={t('shared.actions.backToApp')} />
            </button>
          </SidebarRow>
          {/* Collapsed hides the section headings, so keep a gap here to set the Back-to-app
              action apart from the nav icons (expanded gets this gap from the first heading). */}
          {collapsed && <li className="pt-3" aria-hidden="true" />}

          {/* Rail + mobile list share one source of truth (useSettingsNavSections). */}
          {navSections.map((section) => (
            <Fragment key={section.titleKey}>
              <SidebarSection title={t(section.titleKey)} />
              {section.items.map((item) => {
                const link = (
                  <SettingsNavLink
                    key={item.panel}
                    icon={item.icon}
                    label={t(item.labelKey)}
                    companyId={companyId}
                    panel={item.panel}
                  />
                )
                return item.permissions
                  ? <PermissionGuard key={item.panel} permissions={item.permissions}>{link}</PermissionGuard>
                  : link
              })}
              {/* Install row sits at the end of the account group (Chromium desktop; iOS is app-mode). */}
              {section.titleKey === 'nav.myAccountSettings' && pwa.installable && (
                <SidebarRow>
                  <button
                    type="button"
                    className={cn(sidebarRowClass(collapsed), 'border-0 w-100')}
                    onClick={() => { if (pwa.canInstall) void pwa.install() }}
                  >
                    <SidebarRowContent icon={Download} label={tGeneral('install.settingsAction')} />
                  </button>
                </SidebarRow>
              )}
            </Fragment>
          ))}
        </SidebarGroup>
      </CollapsibleSidebar>

      {/* Settings content area */}
      <div className="col vh-100 overflow-y-auto mw-100" style={{ zIndex: 1 }}>
        <div className="container-fluid px-0 h-100">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
